import { Box, Fade } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"
import { pluralize } from "utils/stringUtils"

import { useGetShipFromTransitTime } from "../hooks/useGetShipFromTransitTime"

import { CompanyPreferenceSection } from "./CompanyPreferenceSection"
import { PreferenceTwoColumnRow } from "./PreferenceTwoColumnRow"
import { PreferredBadge } from "./PreferredBadge"
import { EditShipFromTransitTimeDialog } from "./ship-from/EditShipFromTransitTimeDialog"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"

interface ShipFromTransitTimeProps {
  locationId: string
}

export const ShipFromTransitTime: FC<ShipFromTransitTimeProps> = ({ locationId }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { data, isFetching, refetch } = useGetShipFromTransitTime(locationId)
  const { hasPermissions } = usePermissionManager()

  const canEdit = hasPermissions([PermissionActionType.CUSTOMER_MAINTENANCE])

  const handleOpenDialog = () => {
    setIsDialogOpen(true)
  }

  const handleCloseDialog = (shouldRefetch?: boolean) => {
    setIsDialogOpen(false)
    if (shouldRefetch) {
      refetch()
    }
  }

  if (!data) {
    return (
      <>
        <Fade in unmountOnExit timeout={500}>
          <InformationMissingAlert
            title={canEdit ? "Start by entering a ship from location" : "No configuration details have been added"}
            action={
              canEdit && (
                <Button
                  color='inherit'
                  size='small'
                  appearance='outlined'
                  className='text-primary'
                  startIcon={<AddOutlined />}
                  onClick={handleOpenDialog}
                  data-testid='ship-from-transit-time-add-button'
                >
                  Add Ship From Location
                </Button>
              )
            }
          />
        </Fade>

        {isDialogOpen && <EditShipFromTransitTimeDialog onClose={handleCloseDialog} locationId={locationId} />}
      </>
    )
  }

  return (
    <>
      <CompanyPreferenceSection
        title='Ship From / Transit Time'
        onEdit={handleOpenDialog}
        canEdit={canEdit}
        editButtonTestId='ship-from-transit-time-edit-button'
        isLoading={isFetching}
      >
        <Box>
          <PreferenceTwoColumnRow
            leftContent='Ship From'
            rightContent='Estimated Transit Days'
            isHeader
            shouldShowDivider
          />
          <Box className='flex flex-col'>
            {data?.rows.map((row, index) => (
              <PreferenceTwoColumnRow
                key={row.shipFromWarehouseId}
                leftContent={row.warehouseName || "Unknown Warehouse"}
                rightContent={
                  row.estimatedTransitDays !== null
                    ? `${row.estimatedTransitDays} ${pluralize(row.estimatedTransitDays, "day", "days")}`
                    : "-"
                }
                badge={row.isPreferred ? <PreferredBadge testId='ship-from-preferred-chip' /> : undefined}
                shouldShowDivider={index < data.rows.length - 1}
                testId='ship-from-row'
              />
            ))}
          </Box>
        </Box>
      </CompanyPreferenceSection>

      {isDialogOpen && (
        <EditShipFromTransitTimeDialog onClose={handleCloseDialog} locationId={locationId} initialData={data?.rows} />
      )}
    </>
  )
}
